import React from 'react';
import { X, Calendar, Building2, Utensils, Users, DollarSign } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Modal } from '@/components/ui/Modal/Modal';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card/Card';
import { VStack } from '@/components/ui/Stack';
import { Badge } from '@/components/ui/Badge/Badge';

interface ProposalBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposalData: any; // Full Cvent proposal data from JSONB
  venueName: string;
  currencyCode: string;
  proposalSummary?: string | null; // AI-generated formatted summary
}

export function ProposalBreakdownModal({
  isOpen,
  onClose,
  proposalData,
  venueName,
  currencyCode = 'USD',
  proposalSummary,
}: ProposalBreakdownModalProps) {
  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '-';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currencyCode} ${amount.toLocaleString()}`;
    }
  };

  const formatPercent = (percent: number | null | undefined): string => {
    if (percent === null || percent === undefined) return '-';
    return `${percent.toFixed(2)}%`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  // Extract summary if passed directly or through proposalData
  const summary = proposalSummary || proposalData?._summary || null;
  
  // Extract data from proposal
  const proposal = proposalData?.proposal || {};
  const eventAvailability = proposal.eventAvailability || {};
  const sleepingRoomAvailability = proposal.sleepingRoomAvailability || {};
  const meetingSpaceAvailability = proposal.meetingSpaceAvailability || {};

  // Dates
  const startDate = eventAvailability.startDate || proposal.agendaStartDate;
  const endDate = eventAvailability.endDate;
  const availability = eventAvailability.availability || '';

  // Room rates and details
  const sleepingRooms = proposal.sleepingRooms || [];
  const lowestRoomRate = proposal.proposalDerivedDetail?.lowestRoomRate;
  const maxRoomRate = proposal.proposalDerivedDetail?.maxRoomRate;
  const roomAvailabilityInfo = sleepingRoomAvailability.additionalInfo || '';
  const sleepingRoomReqMet = sleepingRoomAvailability.sleepingRoomReqMetType || '';

  // Taxes and fees
  const applicableTaxPercent = proposal.applicableTaxPercent ?? null;
  const serviceChargePercent = proposal.serviceChargePercent ?? null;
  const isTaxInclusive = proposal.isTaxInclusive ?? false;
  const taxAdditionalInfo = proposal.taxAdditionalInfo || '';

  // F&B
  const foodBeverageMinimum = proposal.foodBeverageMinimum ?? null;
  const foodBeverageMinimumNote = proposal.foodBeverageMinimumNote || '';
  const isFnbCost = proposal.isFnbCost ?? false;
  const estimatedCostsAdditionalInfo = proposal.estimatedCostsAdditionalInfo || '';

  // Meeting room costs
  const meetingRoomInfo = meetingSpaceAvailability.additionalInfo || '';
  const avNeeds = meetingSpaceAvailability.avNeeds || '';

  // Parse room details from additionalInfo if available
  const parseRoomDetails = (info: string) => {
    // Try to extract room counts and rates from the text
    // This is a best-effort parsing since the format may vary
    const lines = info.split('\n').filter(line => line.trim());
    return lines;
  };

  const roomDetailsLines = parseRoomDetails(roomAvailabilityInfo);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Cost Breakdown - ${venueName}`}
      size="lg"
    >
      <VStack gap={4}>
        {/* Dates */}
        {(startDate || endDate) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Event Dates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {startDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date:</span>
                    <span className="font-medium">{formatDate(startDate)}</span>
                  </div>
                )}
                {endDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Date:</span>
                    <span className="font-medium">{formatDate(endDate)}</span>
                  </div>
                )}
                {startDate && endDate && (
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium">
                      {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))} nights
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cvent Proposal Summary */}
        {summary && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Cvent Summary
              </CardTitle>
              <CardDescription>
                AI-generated formatted breakdown of proposal costs and details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-strong:font-semibold prose-ul:text-foreground prose-li:text-foreground">
                <ReactMarkdown
                  components={{
                    h1: ({...props}: any) => <h1 className="text-xl font-semibold mb-3 mt-4 first:mt-0" {...props} />,
                    h2: ({...props}: any) => <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0" {...props} />,
                    h3: ({...props}: any) => <h3 className="text-base font-semibold mb-2 mt-2 first:mt-0" {...props} />,
                    p: ({...props}: any) => <p className="mb-2 text-sm leading-relaxed" {...props} />,
                    ul: ({...props}: any) => <ul className="list-disc list-inside mb-2 space-y-1 ml-2" {...props} />,
                    ol: ({...props}: any) => <ol className="list-decimal list-inside mb-2 space-y-1 ml-2" {...props} />,
                    li: ({...props}: any) => <li className="text-sm" {...props} />,
                    strong: ({...props}: any) => <strong className="font-semibold" {...props} />,
                    em: ({...props}: any) => <em className="italic" {...props} />,
                  }}
                >
                  {summary}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Room Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Room Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VStack gap={4}>
              {/* Dates and Rate Summary */}
              {startDate && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground font-medium">Dates:</span>
                    <span className="font-semibold">
                      {formatDate(startDate)}
                      {endDate && ` - ${formatDate(endDate)}`}
                    </span>
                  </div>
                  {(lowestRoomRate !== null && lowestRoomRate !== undefined) && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Rate:</span>
                      <div className="flex items-center gap-2">
                        {lowestRoomRate && (
                          <span className="font-semibold">
                            {formatCurrency(lowestRoomRate)}
                          </span>
                        )}
                        {lowestRoomRate && maxRoomRate && lowestRoomRate !== maxRoomRate && (
                          <>
                            <span className="text-muted-foreground">-</span>
                            <span className="font-semibold">
                              {formatCurrency(maxRoomRate)}
                            </span>
                          </>
                        )}
                        <span className="text-xs text-muted-foreground">per room, per night</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Room Details from sleepingRooms array */}
              {sleepingRooms.length > 0 && (
                <div className="pt-2 border-t">
                  <h4 className="font-medium mb-3 text-sm">Room Categories & Availability:</h4>
                  <div className="space-y-3">
                    {sleepingRooms.map((room: any, index: number) => (
                      <div key={index} className="p-3 bg-muted rounded border">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          <div>
                            {room.categoryName && (
                              <div className="font-medium mb-1">{room.categoryName}</div>
                            )}
                            {room.numberOfRooms && (
                              <div className="text-muted-foreground text-xs">
                                {room.numberOfRooms} room{room.numberOfRooms !== 1 ? 's' : ''} available
                              </div>
                            )}
                          </div>
                          <div>
                            {(room.startDate || room.endDate) && (
                              <div className="text-xs text-muted-foreground mb-1">Dates:</div>
                            )}
                            {room.startDate && room.endDate && (
                              <div className="font-medium text-xs">
                                {formatDate(room.startDate)} - {formatDate(room.endDate)}
                              </div>
                            )}
                            {room.startDate && !room.endDate && (
                              <div className="font-medium text-xs">{formatDate(room.startDate)}</div>
                            )}
                          </div>
                          <div className="text-right">
                            {(room.rate || room.averageDailyRate) && (
                              <div className="text-xs text-muted-foreground mb-1">Rate:</div>
                            )}
                            {room.rate && (
                              <div className="font-semibold">{formatCurrency(room.rate)}</div>
                            )}
                            {room.averageDailyRate && !room.rate && (
                              <div className="font-semibold">{formatCurrency(room.averageDailyRate)}</div>
                            )}
                            {room.lowestRate && (
                              <div className="text-xs text-muted-foreground">From {formatCurrency(room.lowestRate)}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Room Details from additionalInfo text (fallback if no array data) */}
              {roomAvailabilityInfo && sleepingRooms.length === 0 && (
                <div className="pt-2 border-t">
                  <h4 className="font-medium mb-2 text-sm">Room Availability Details:</h4>
                  <div className="p-3 bg-muted rounded text-sm whitespace-pre-wrap border">
                    {roomAvailabilityInfo}
                  </div>
                </div>
              )}

              {/* Taxes and Fees */}
              {(applicableTaxPercent !== null || serviceChargePercent !== null || taxAdditionalInfo) && (
                <div className="pt-2 border-t">
                  <h4 className="font-medium mb-2 text-sm">Taxes & Fees:</h4>
                  <div className="space-y-2 text-sm">
                    {applicableTaxPercent !== null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax:</span>
                        <span className="font-medium">
                          {formatPercent(applicableTaxPercent)}
                          {isTaxInclusive && <span className="text-xs text-muted-foreground ml-1">(inclusive)</span>}
                        </span>
                      </div>
                    )}
                    {serviceChargePercent !== null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Service Charge:</span>
                        <span className="font-medium">{formatPercent(serviceChargePercent)}</span>
                      </div>
                    )}
                    {taxAdditionalInfo && (
                      <div className="mt-2 p-2 bg-muted/50 rounded text-xs whitespace-pre-wrap border">
                        {taxAdditionalInfo}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </VStack>
          </CardContent>
        </Card>

        {/* Food & Beverage */}
        {(foodBeverageMinimum !== null || isFnbCost || estimatedCostsAdditionalInfo || applicableTaxPercent !== null || serviceChargePercent !== null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Utensils className="w-5 h-5" />
                Food & Beverage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VStack gap={3}>
                {foodBeverageMinimum !== null && (
                  <div className="pb-2 border-b">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground font-medium">F&B Minimum:</span>
                      <span className="font-semibold text-lg">{formatCurrency(foodBeverageMinimum)}</span>
                    </div>
                    {foodBeverageMinimumNote && (
                      <p className="text-xs text-muted-foreground mt-1">{foodBeverageMinimumNote}</p>
                    )}
                  </div>
                )}

                {/* F&B Taxes and Service Charges */}
                {(applicableTaxPercent !== null || serviceChargePercent !== null) && (
                  <div>
                    <h4 className="font-medium mb-2 text-sm">F&B Charges:</h4>
                    <div className="space-y-2 text-sm">
                      {serviceChargePercent !== null && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Service Fee:</span>
                          <span className="font-medium">{formatPercent(serviceChargePercent)}</span>
                        </div>
                      )}
                      {applicableTaxPercent !== null && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">F&B Tax:</span>
                          <span className="font-medium">{formatPercent(applicableTaxPercent)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {estimatedCostsAdditionalInfo && (
                  <div className="pt-2 border-t">
                    <h4 className="font-medium mb-2 text-sm">Additional F&B Information:</h4>
                    <div className="p-3 bg-muted rounded text-sm whitespace-pre-wrap border">
                      {estimatedCostsAdditionalInfo}
                    </div>
                  </div>
                )}
              </VStack>
            </CardContent>
          </Card>
        )}

        {/* Meeting Room Costs */}
        {(meetingRoomInfo || avNeeds) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Meeting Room Costs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VStack gap={3}>
                {meetingRoomInfo && (
                  <div>
                    <h4 className="font-medium mb-2 text-sm">Meeting Space Details:</h4>
                    <div className="p-3 bg-muted rounded text-sm whitespace-pre-wrap">
                      {meetingRoomInfo}
                    </div>
                  </div>
                )}
                {avNeeds && (
                  <div>
                    <h4 className="font-medium mb-2 text-sm">AV Requirements:</h4>
                    <div className="p-3 bg-muted rounded text-sm whitespace-pre-wrap">
                      {avNeeds}
                    </div>
                  </div>
                )}
              </VStack>
            </CardContent>
          </Card>
        )}

        {/* Additional Information */}
        {proposal.billingContractualResponse && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Billing & Contractual Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-muted rounded text-sm whitespace-pre-wrap">
                {proposal.billingContractualResponse}
              </div>
            </CardContent>
          </Card>
        )}
      </VStack>
    </Modal>
  );
}

